<template>
  <div class="department-dashboard">
    <!-- Department Header Area -->
    <header class="department-header md-layout md-alignment-center-space-between">
      <div>
        <h2 class="md-title">{{ departmentName }}</h2>
        <p class="md-subhead text-muted">{{ departmentDescription }}</p>
      </div>
      <div class="header-actions" v-if="isLoading">
        <md-progress-spinner md-mode="indeterminate" :md-diameter="24" :md-stroke="2"></md-progress-spinner>
      </div>
    </header>

    <!-- Key Metrics Grid -->
    <section class="metrics-grid">
      <div v-for="metric in metricCards" :key="metric.label" :class="['metric-card', metric.variant]">
        <div class="metric-icon">
          <md-icon>{{ metric.icon }}</md-icon>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ metric.formattedValue }}</div>
          <div class="metric-label">{{ metric.label }}</div>
        </div>
      </div>
    </section>

    <!-- NEW SECTION: Cost Split Indicators Indicator Bar -->
    <section class="md-layout md-gutter mb-4" v-if="totalCost > 0">
      <div class="md-layout-item md-size-100">
        <md-card class="dashboard-card cost-split-card">
          <md-card-content>
            <div class="md-layout md-alignment-center-space-between mb-2">
              <span class="cost-split-title"><b>Financial Operations Breakdown</b></span>
              <span class="cost-split-total">Total: <b>{{ formatCurrency(totalCost) }}</b></span>
            </div>
            <!-- Split Visualization Track -->
            <div class="split-progress-track">
              <div class="split-segment standard-bg" :style="{ width: costPercentages.standard + '%' }">
                <md-tooltip md-direction="top">Standard Days Cost</md-tooltip>
              </div>
              <div class="split-segment weekend-bg" :style="{ width: costPercentages.weekend + '%' }">
                <md-tooltip md-direction="top">Weekend Premium Cost</md-tooltip></div>
            </div>
            <!-- Labels Subrow Grid -->
            <div class="md-layout md-gutter mt-2 label-row text-small">
              <div class="md-layout-item d-flex align-items-center">
                <span class="indicator-dot standard-bg"></span>
                <span>Standard Weekday Allocation: <b>{{
                    formatCurrency(standardCost)
                  }}</b> ({{ costPercentages.standard }}%)</span>
              </div>
              <div class="md-layout-item d-flex align-items-center justify-content-end">
                <span class="indicator-dot weekend-bg"></span>
                <span>Weekend Premium Allocation: <b>{{ formatCurrency(weekendCost) }}</b> ({{
                    costPercentages.weekend
                  }}%)</span>
              </div>
            </div>
          </md-card-content>
        </md-card>
      </div>
    </section>

    <!-- Section: Visualizations -->
    <section class="md-layout md-gutter">
      <div class="md-layout-item md-size-33 md-medium-size-50 md-small-size-100">
        <md-card class="dashboard-card">
          <md-card-header>
            <div class="md-title">Guidance Status</div>
          </md-card-header>
          <md-card-content class="chart-container">
            <canvas ref="statusChartCanvas"></canvas>
          </md-card-content>
        </md-card>
      </div>

      <!-- NEW CHART: Vendor Cost Expenditure Share Allocation Matrix -->
      <div class="md-layout-item md-size-33 md-medium-size-50 md-small-size-100">
        <md-card class="dashboard-card">
          <md-card-header>
            <div class="md-title">Vendor Cost Distribution</div>
          </md-card-header>
          <md-card-content class="chart-container">
            <canvas ref="vendorChartCanvas"></canvas>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-34 md-medium-size-100 md-small-size-100">
        <md-card class="dashboard-card">
          <md-card-header>
            <div class="md-title">Expenditures by Timeline Pool</div>
          </md-card-header>
          <md-card-content class="chart-container">
            <canvas ref="requestsChartCanvas"></canvas>
          </md-card-content>
        </md-card>
      </div>
    </section>

    <!-- Recent Guidances Table -->
    <section class="md-layout md-gutter mt-4">
      <div class="md-layout-item md-size-100">
        <md-card class="dashboard-card">
          <md-card-header class="md-layout md-alignment-center-space-between">
            <div class="md-title">Recent Guidances</div>
            <md-button class="md-primary md-dense" @click="viewAllGuidances">
              View All
            </md-button>
          </md-card-header>
          <md-card-content>
            <md-table v-model="recentGuidances" md-card>
              <md-table-row slot="md-table-row" slot-scope="{ item }">

                <md-table-cell md-label="Guidance #">
                  <clickable-link
                      :id="item.id"
                      resource="Guidance"
                      :text="item.guidance_number"
                      :stop-propagation="true"
                  /></md-table-cell>
                <md-table-cell md-label="Name">{{ item.name }}</md-table-cell>
                <md-table-cell md-label="Duration">
                  {{item.date_range}}<br/>
                  <badge :type="getStatusBadge(item.duration)"> {{ item.duration }}</badge>

                </md-table-cell>
                <md-table-cell md-label="Labourers" md-numeric>{{ item.labourer_count }}</md-table-cell>
                <md-table-cell md-label="Vendor">
                  <clickable-link
                      :id="item.vendor_uuid"
                      resource="Vendor"
                      :text="item.vendor_name"
                      :stop-propagation="true"
                  /></md-table-cell>
                <md-table-cell md-label="Status">
                  <badge :type="getStatusBadge(item.status)">{{ item.status }}</badge>
                </md-table-cell>
                <md-table-cell md-label="Progress">
                  <div class="progress-cell">
                    <md-progress-bar :md-value="item.progress" md-mode="determinate"></md-progress-bar>
                    <span>{{ item.progress }}%</span>
                  </div>
                </md-table-cell>
              </md-table-row>
            </md-table>
          </md-card-content>
        </md-card>
      </div>
    </section>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import Badge from '@/components/Badge.vue';
import {mapGetters} from 'vuex';

var STATUS_COLOR_PALETTE = ['#9e9e9e', '#2196f3', '#ff9800', '#4caf50', '#009688'];
var VENDOR_COLOR_PALETTE = ['#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#00bcd4', '#ffc107'];

export default {
  name: 'DepartmentDashboard',
  components: {Badge},
  props: {
    dateRange: {
      type: Object,
      default: function () {
        return {start: null, end: null};
      }
    }
  },
  data: function () {
    return {
      departmentId: null,
      departmentName: '',
      departmentDescription: '',
      totalGuidances: 0,
      activeGuidances: 0,
      totalLabourers: 0,
      completedGuidances: 0,
      totalCost: 0,
      standardCost: 0,
      weekendCost: 0,
      vendorSpendMap: [],
      recentGuidances: [],
      charts: {
        status: null,
        requests: null,
        vendor: null
      }
    };
  },
  computed: {
    ...mapGetters('dashboard', ['isLoading']),
    me: function () {
      return (this.$store.state.profile && this.$store.state.profile.me) ? this.$store.state.profile.me : null;
    },
    costPercentages: function () {
      if (this.totalCost <= 0) return {standard: 0, weekend: 0};
      return {
        standard: Math.round((this.standardCost / this.totalCost) * 100),
        weekend: Math.round((this.weekendCost / this.totalCost) * 100)
      };
    },
    metricCards: function () {
      return [
        {
          label: 'Total Expenditures',
          formattedValue: this.formatCurrency(this.totalCost),
          icon: 'payments',
          variant: 'rose-card'
        },
        {
          label: 'Active Guidances',
          formattedValue: this.formatNumber(this.activeGuidances),
          icon: 'play_circle',
          variant: 'success'
        },
        {
          label: 'Total Labourers Assigned',
          formattedValue: this.formatNumber(this.totalLabourers),
          icon: 'people',
          variant: 'warning'
        },
        {
          label: 'Completed Jobs',
          formattedValue: this.formatNumber(this.completedGuidances),
          icon: 'check_circle',
          variant: 'primary'
        }
      ];
    }
  },
  created: function () {
    this.fetchDepartmentData();
  },
  watch: {
    me: {
      handler: function (val) {
        if (!val) return;
        var targetId = (val.model && val.model.department) ? val.model.department.id : null;
        if (targetId && targetId !== this.departmentId) {
          this.departmentId = targetId;
          this.fetchDepartmentData();
        }
      },
      deep: true,
      immediate: true
    },
    dateRange: {
      handler: function () {
        this.fetchDepartmentData();
      },
      deep: true
    }
  },
  methods: {
    async fetchDepartmentData() {
      if (!this.departmentId) return;

      try {
        var params = {
          department_id: this.departmentId,
          start_date: this.dateRange ? this.dateRange.start : null,
          end_date: this.dateRange ? this.dateRange.end : null
        };

        await this.$store.dispatch('dashboard/fetchDepartmentStats', params);
        var data = this.$store.getters['dashboard/departmentStats'];

        if (data) {
          this.departmentName = data.department_name || 'Department Dashboard';
          this.departmentDescription = data.department_description || '';
          this.totalGuidances = data.total_guidances || 0;
          this.activeGuidances = data.active_guidances || 0;
          this.totalLabourers = data.total_labourers || 0;
          this.completedGuidances = data.completed_guidances || 0;

          // Map dynamic newly added cost metrics properties securely
          this.totalCost = data.total_department_cost || 0;
          this.standardCost = data.standard_cost_slice || 0;
          this.weekendCost = data.weekend_cost_slice || 0;
          this.vendorSpendMap = data.vendor_spend_map || [];
          this.recentGuidances = data.recent_guidances || [];

          this.$nextTick(function () {
            this.renderCharts(data);
          }.bind(this));
        }
      } catch (error) {
        console.error('[DepartmentDashboard] Execution Error:', error);
      }
    },

    renderCharts: function (data) {
      this.destroyCharts();
      this.buildStatusChart(data);
      this.buildRequestsChart(data);
      this.buildVendorCostChart(data);
    }, destroyCharts: function () {
      const self = this;
      Object.keys(this.charts).forEach(function (key) {
        if (self.charts[key]) {
          self.charts[key].destroy();
          self.charts[key] = null;
        }
      });
    }, buildStatusChart: function (data) {
      let el = this.$refs.statusChartCanvas;
      if (!el) return;
      let  statusCounts = data.status_counts || {};
      this.charts.status = new Chart(el.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: ['Created', 'Reviewed', 'Approved', 'Accepted', 'Completed'],
          datasets: [{
            data: [statusCounts.Created || 0, statusCounts.Reviewed || 0, statusCounts.Approved || 0, statusCounts.Accepted || 0, statusCounts.Completed || 0],
            backgroundColor: STATUS_COLOR_PALETTE,
            borderWidth: 0
          }]
        },
        options: {responsive: true, maintainAspectRatio: false}
      });
    }, buildVendorCostChart: function (data) {
      let  el = this.$refs.vendorChartCanvas;
      if (!el) return;
      let  dataMap = data.vendor_spend_map || [];
      let  labels = dataMap.map(function (v) {
        return v.name;
      });
      let  spendData = dataMap.map(function (v) {
        return v.total_spend;
      });
      this.charts.vendor = new Chart(el.getContext('2d'), {
        type: 'polarArea',
        data: {labels: labels, datasets: [{data: spendData, backgroundColor: VENDOR_COLOR_PALETTE, borderWidth: 1}]},
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  return ' ' + context.label + ': $' + context.raw.toLocaleString();
                }
              }
            }
          }
        }
      });
    }, buildRequestsChart: function (data) {
      let  el = this.$refs.requestsChartCanvas;
      if (!el) return;
      this.charts.requests = new Chart(el.getContext('2d'), {
        type: 'line',
        data: {
          labels: data.trend_labels || [],
          datasets: [{
            label: 'Cost (TZS)',
            data: data.trend_data || [],
            borderColor: '#009688',
            backgroundColor: 'rgba(0, 150, 136, 0.05)',
            fill: true,
            tension: 0.3
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  return ' Spend: $' + context.raw.toLocaleString();
                }
              }
            }
          }
        }
      });
    }, formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'TZS'}).format(value || 0);
    }, formatNumber(value) {
      return (value || 0).toLocaleString();
    }, getStatusBadge(status) {
      if (status === 'Completed' || status === 'Month' ) return 'success';
      if (status === 'Active' || status === 'Accepted'  || status === 'Week') return 'primary';
      if (status === 'Reviewed'  || status === 'Day') return 'rose';
      return 'info';
    }, viewAllGuidances() {
      this.$router.push({name: 'guidances-list'});
    }
  }
}
</script>
<style scoped>
.mb-4 { margin-bottom: 1.5rem; }
.mt-4 { margin-top: 1.5rem; }
.progress-cell { display: flex; align-items: center; gap: 10px; min-width: 120px; }
.progress-cell span { font-size: 0.8rem; font-weight: bold; }
.metrics-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1.5rem; margin-bottom: 2rem; }
.metric-card { display: flex; align-items: center; padding: 1.25rem; background: #fff; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.metric-icon { font-size: 2rem; margin-right: 1rem; color: #555; }
.metric-value { font-size: 1.5rem; font-weight: bold; }
.metric-label { font-size: 0.85rem; color: #888; }
.rose-card .metric-icon { color: #e91e63; }
/* Micro Split Component Styling Track Layouts */
.cost-split-card { background: #fafafa !important; border: 1px solid #e0e0e0; box-shadow: none !important; }
.split-progress-track { display: flex; width: 100%; height: 12px; background: #eee; border-radius: 6px; overflow: hidden; margin: 0.5rem 0; }
.split-segment { height: 100%; transition: width 0.4s ease; }
.standard-bg { background-color: #2196f3; }
.weekend-bg { background-color: #ff9800; }
.indicator-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; }
.d-flex { display: flex; }
.align-items-center { align-items: center; }
.justify-content-end { justify-content: flex-end; }
</style>